import { GlslModule } from '@webgl/glsl/glslModule'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'

import { reportError } from '@/instrumentation'

/**
 * nanogl only logs shader compile/link failures when `Program.debug` is true (off in production),
 * so failures otherwise surface as a silent black screen, or as missing uniform setters
 * (`prg.uTime is not a function`). Compile and report them to Sentry instead.
 *
 * Uses `compile()`'s return value: `prg.ready` only flips on the first `use()`, so it can't tell
 * a failed compile from one that hasn't been used yet.
 */
export function compileProgram(
  gl: GLContext,
  prg: Program,
  vert: string,
  frag: string,
  prefix?: string,
  id?: string
): Program {
  if (prg.compile(vert, frag, prefix)) return prg
  // On a lost context every compile fails and the GL objects are null. GLView already reports the
  // loss once; reporting here too would send one event per program.
  if (gl.isContextLost()) return prg
  // A shader compile failure returns before linking, leaving the program log empty; the compiler
  // messages live on the shaders themselves.
  const log = gl.getProgramInfoLog(prg.program)
  const vertLog = gl.getShaderInfoLog(prg.vShader)
  const fragLog = gl.getShaderInfoLog(prg.fShader)
  reportError(new Error('Shader program compilation failed'), { id, prefix, log, vertLog, fragLog })
  return prg
}

/// #if _DEBUG

/**
 * Wrap a glsl import module to a GlslModule which return up to date code when HMR is triggered
 * @param glslModule
 * @returns
 */
export default function CreateShader(glslModule: GlslModule): GlslModule {
  let uptodateModule = glslModule
  glslModule.onHmr((m) => (uptodateModule = m))
  const fn: GlslModule = function (o?: unknown) {
    return uptodateModule(o)
  }
  fn.toString = fn
  fn.onHmr = (l) => uptodateModule.onHmr(l)
  return fn
}

/**
 * Return a program which automatically recompile when one of its shader is updated by HMR.
 * @param gl
 * @param vert
 * @param frag
 * @param prefix
 * @returns
 */
export function CreateProgram(
  gl: GLContext,
  vert: GlslModule,
  frag: GlslModule,
  prefix?: string,
  datas?: unknown
): Program {
  const prg = new Program(gl)
  const lv = CreateShader(vert)
  const lf = CreateShader(frag)
  const compile = () => compileProgram(gl, prg, lv(datas), lf(datas), prefix)
  compile()
  lv.onHmr(compile)
  lf.onHmr(compile)
  return prg
}

/*/
/// #else
export default function CreateShader(glslModule:GlslModule ): GlslModule { return glslModule }
export function CreateProgram( gl:GLContext, vert:GlslModule, frag:GlslModule, defs?:string, datas?: unknown ): Program { return compileProgram(gl, new Program(gl), vert(datas), frag(datas), defs) }

/// #endif
//*/
